import { useEffect, useState } from "react";
import { PageHeader, Pill } from "@/components/ui-bits";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useConstants } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";

export default function CreateTest() {
  const constants = useConstants();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [questions, setQuestions] = useState([]);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [selected, setSelected] = useState([]);
  const [form, setForm] = useState({
    title: "", duration: 15, passing_marks: 60, course_id: "", batch_id: "", scheduled_at: "",
    allow_reattempts: true, max_attempts: 2,
    proctoring_enabled: true, violation_threshold: 2,
    upload_questions_after: false,
  });
  const [error, setError] = useState("");
  const [filter, setFilter] = useState({ topic: "", assessment_id: "", chapter: "", module_id: "" });
  const [modules, setModules] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/questions").then(r => setQuestions(r.data));
    api.get("/courses").then(r => setCourses(r.data));
    api.get("/batches").then(r => setBatches(r.data));
    api.get("/assessments").then(r => setAssessments(r.data));
    api.get("/modules").then(r => setModules(r.data));
  }, []);

  const toggle = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x!==id) : [...s, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (selected.length === 0 && !form.upload_questions_after) {
      setError("Select at least one question, or enable 'Upload questions after creating'");
      return;
    }
    try {
      const { data } = await api.post("/assessments", {
        title: form.title,
        duration: Number(form.duration),
        passing_marks: Number(form.passing_marks),
        course_id: form.course_id || null,
        batch_id: form.batch_id || null,
        question_ids: selected,
        scheduled_at: form.scheduled_at || null,
        allow_reattempts: !!form.allow_reattempts,
        max_attempts: form.allow_reattempts && form.max_attempts !== "" && form.max_attempts !== null
          ? Number(form.max_attempts)
          : null,
        proctoring_enabled: !!form.proctoring_enabled,
        violation_threshold: Number(form.violation_threshold) || 2,
      });
      // If user opted to author questions specifically for this test, jump to the upload page.
      if (form.upload_questions_after) {
        navigate(`/trainer/assessments/${data.id}/questions`);
      } else {
        navigate("/trainer/results");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create");
    }
  };

  const assessmentQIds = filter.assessment_id
    ? new Set((assessments.find(a => a.id === filter.assessment_id)?.question_ids) || [])
    : null;

  // Questions that belong to the currently selected module (or all if none selected)
  const questionsInScope = filter.module_id
    ? questions.filter(q => q.module_id === filter.module_id)
    : questions;

  const filtered = questionsInScope.filter(q =>
    (!filter.topic || q.topic === filter.topic) &&
    (!filter.chapter || q.chapter === filter.chapter) &&
    (!assessmentQIds || assessmentQIds.has(q.id))
  );

  // Topic + Chapter dropdowns are derived FROM questionsInScope so they only surface
  // options that actually exist under the selected module.
  const topics = [...new Set(questionsInScope.map(q => q.topic).filter(Boolean))];
  const chaptersInScope = (constants.chapters || []).filter(
    c => questionsInScope.some(q => q.chapter === c)
  );

  const selectAllFiltered = () => {
    const ids = filtered.map(q => q.id);
    setSelected(s => Array.from(new Set([...s, ...ids])));
  };
  const clearSelection = () => setSelected([]);

  return (
    <div>
      <PageHeader title="Create Test" subtitle="Configure a timed assessment and pick questions from the bank." />

      <form onSubmit={submit} className="space-y-6">
        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Title">
              <input data-testid="ct-title" required value={form.title} onChange={e=>setForm({...form, title: e.target.value})} className={input} />
            </Field>
            <Field label="Duration (minutes)">
              <input data-testid="ct-duration" required type="number" min={1} value={form.duration} onChange={e=>setForm({...form, duration: e.target.value})} className={input} />
            </Field>
            <Field label="Passing marks (%)">
              <input data-testid="ct-passing" required type="number" min={0} max={100} value={form.passing_marks} onChange={e=>setForm({...form, passing_marks: e.target.value})} className={input} />
            </Field>
            <Field label="Course">
              <select value={form.course_id} onChange={e=>setForm({...form, course_id: e.target.value})} className={input}>
                <option value="">— Any —</option>
                {courses.map(c => <option key={c.id} value={c.id}>{c.course_name}</option>)}
              </select>
            </Field>
            <Field label="Batch">
              <select value={form.batch_id} onChange={e=>setForm({...form, batch_id: e.target.value})} className={input}>
                <option value="">— Any —</option>
                {batches.map(b => <option key={b.id} value={b.id}>{b.batch_name}</option>)}
              </select>
            </Field>
            <Field label="Scheduled at (optional)">
              <input type="datetime-local" value={form.scheduled_at} onChange={e=>setForm({...form, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : ""})} className={input} />
            </Field>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="text-sm font-semibold text-slate-900 mb-3 flex items-center gap-2">
              Re-attempt policy
              {!isAdmin && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
                  Locked · Admin only
                </span>
              )}
            </div>
            <label className={`flex items-start gap-3 ${isAdmin ? "cursor-pointer" : "cursor-not-allowed opacity-70"}`}>
              <input
                type="checkbox"
                data-testid="ct-allow-reattempts"
                checked={form.allow_reattempts}
                disabled={!isAdmin}
                onChange={e => setForm({ ...form, allow_reattempts: e.target.checked })}
                className="mt-1 w-4 h-4 accent-blue-600"
              />
              <span className="text-sm text-slate-700">
                <span className="font-medium text-slate-900">Allow re-attempts</span>
                <span className="block text-slate-500 mt-0.5">
                  {isAdmin
                    ? "When off, each employee can submit this assessment exactly once."
                    : "Every employee gets exactly 2 attempts. Only an Admin can change this policy."}
                </span>
              </span>
            </label>
            {form.allow_reattempts && (
              <div className="mt-4 ml-7 max-w-xs">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Max attempts per employee {isAdmin ? "(leave empty for unlimited)" : ""}
                </label>
                <input
                  data-testid="ct-max-attempts"
                  type="number"
                  min={1}
                  placeholder={isAdmin ? "Unlimited" : "2"}
                  value={form.max_attempts}
                  disabled={!isAdmin}
                  onChange={e => setForm({ ...form, max_attempts: e.target.value })}
                  className={`${input} ${!isAdmin ? "bg-slate-50 text-slate-500 cursor-not-allowed" : ""}`}
                />
                {!isAdmin && (
                  <div className="text-xs text-slate-500 mt-1">
                    Default policy: 2 attempts. Contact an Admin to raise or waive this limit.
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 pt-6 border-t border-slate-100">
            <div className="text-sm font-semibold text-slate-900 mb-3">Anti-cheat / Proctoring</div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="ct-proctoring"
                checked={form.proctoring_enabled}
                onChange={e => setForm({ ...form, proctoring_enabled: e.target.checked })}
                className="mt-1 w-4 h-4 accent-blue-600"
              />
              <span className="text-sm text-slate-700">
                <span className="font-medium text-slate-900">Enable proctoring</span>
                <span className="block text-slate-500 mt-0.5">
                  Forces fullscreen, blocks right-click / copy / paste / dev-tool shortcuts, and
                  tracks tab-switches & window blur. Violations are recorded on the result.
                </span>
              </span>
            </label>
            {form.proctoring_enabled && (
              <div className="mt-4 ml-7 max-w-xs">
                <label className="block text-sm font-medium text-slate-700 mb-1.5">
                  Auto-submit after (warnings)
                </label>
                <input
                  data-testid="ct-violation-threshold"
                  type="number"
                  min={1}
                  max={10}
                  value={form.violation_threshold}
                  onChange={e => setForm({ ...form, violation_threshold: e.target.value })}
                  className={input}
                />
              </div>
            )}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-6">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="ct-upload-questions-after"
              checked={form.upload_questions_after}
              onChange={e => setForm({ ...form, upload_questions_after: e.target.checked })}
              className="mt-1 w-4 h-4 accent-blue-600"
            />
            <span className="text-sm text-slate-700">
              <span className="font-medium text-slate-900">Upload questions after creating this test</span>
              <span className="block text-slate-500 mt-0.5">
                Skip picking from the bank and instead bulk-upload CSV/XLSX questions scoped to this specific assessment on the next screen.
              </span>
            </span>
          </label>
        </div>

        <div className={`bg-white border border-slate-200 rounded-xl p-6 ${form.upload_questions_after ? "opacity-50 pointer-events-none" : ""}`}>
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <div>
              <div className="text-base font-semibold text-slate-900">Choose questions</div>
              <div className="text-sm text-slate-500">
                {selected.length} selected · {filtered.length} shown · {questions.length} total
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <select
                data-testid="ct-filter-assessment"
                value={filter.assessment_id}
                onChange={e => setFilter({ ...filter, assessment_id: e.target.value })}
                className={input + " min-w-[220px]"}
                title="Show only questions from this assessment"
              >
                <option value="">All assessments</option>
                {assessments.map(a => (
                  <option key={a.id} value={a.id}>
                    {a.title} ({a.question_ids?.length || 0}q)
                  </option>
                ))}
              </select>
              <select
                data-testid="ct-filter-module"
                value={filter.module_id}
                onChange={e => setFilter({ ...filter, module_id: e.target.value, chapter: "", topic: "" })}
                className={input + " min-w-[180px]"}
                title="Filter questions by module">
                <option value="">All modules</option>
                {modules.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
              <select
                data-testid="ct-filter-topic"
                value={filter.topic}
                onChange={e => setFilter({ ...filter, topic: e.target.value })}
                className={input + " max-w-[180px]"}>
                <option value="">{filter.module_id && topics.length === 0 ? "No topics" : "All topics"}</option>
                {topics.map(t => <option key={t}>{t}</option>)}
              </select>
              <select
                data-testid="ct-filter-chapter"
                value={filter.chapter}
                onChange={e => setFilter({ ...filter, chapter: e.target.value })}
                className={input + " min-w-[200px] max-w-[260px]"}
                title="Filter questions by ICD-10-CM chapter">
                <option value="">
                  {filter.module_id
                    ? (chaptersInScope.length === 0 ? "No chapters in this module" : "All chapters (in module)")
                    : "All chapters"}
                </option>
                {chaptersInScope.map((c) => (
                  <option key={c} value={c} title={c}>
                    {((constants.chapters || []).indexOf(c) + 1)}. {c.length > 40 ? c.slice(0, 40) + "…" : c}
                  </option>
                ))}
              </select>
              <button
                type="button"
                data-testid="ct-select-all-filtered"
                onClick={selectAllFiltered}
                disabled={filtered.length === 0}
                className="text-xs font-medium text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100 rounded-lg px-3 py-2 disabled:opacity-50">
                Select all filtered
              </button>
              {selected.length > 0 && (
                <button
                  type="button"
                  data-testid="ct-clear-selection"
                  onClick={clearSelection}
                  className="text-xs font-medium text-slate-600 border border-slate-200 hover:bg-slate-50 rounded-lg px-3 py-2">
                  Clear
                </button>
              )}
            </div>
          </div>

          <div className="space-y-2 max-h-[500px] overflow-y-auto pr-2">
            {filtered.map(q => (
              <label key={q.id} data-testid={`ct-question-${q.id}`}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${selected.includes(q.id) ? "border-blue-300 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <input type="checkbox" checked={selected.includes(q.id)} onChange={()=>toggle(q.id)} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm text-slate-900">{q.question}</div>
                  <div className="mt-1 flex gap-1.5">
                    {q.topic && <Pill tone="blue">{q.topic}</Pill>}
                  </div>
                </div>
              </label>
            ))}
          </div>
        </div>

        {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-lg">{error}</div>}
        <div className="flex justify-end">
          <button data-testid="ct-submit" type="submit" className="bg-blue-600 text-white rounded-lg px-6 py-2.5 font-medium hover:bg-blue-700">
            {form.upload_questions_after ? "Create & upload questions →" : "Create assessment"}
          </button>
        </div>
      </form>
    </div>
  );
}

const input = "w-full border border-slate-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none text-slate-900 bg-white";
function Field({ label, children }) {
  return <div><label className="block text-sm font-medium text-slate-700 mb-1.5">{label}</label>{children}</div>;
}
